import {
    Account,
    GroupedLabelFilters,
    GroupedServiceLabelFilter,
    LabelSummary,
} from 'models';
import { LabelableType } from '../models/mixins/Labelable';
import { intersection } from './array';

/**
 * We cannot include parse objects in a call to a cloud function.
 * Label summaries include pointers to their full Label Objects so we need to remove those pointers.
 */
export const sanitizeLabels = (
    labels: Partial<LabelSummary>[] | undefined
): Omit<LabelSummary, 'label'>[] | undefined => {
    return labels?.map((l) => {
        const { label, path, ...rest } = l;
        return { ...rest, path: sanitizeLabels(path) };
    }) as unknown as Omit<LabelSummary, 'label'>[] | undefined;
};

export const getLabelIntersection = (
    primary: LabelableType,
    secondary: LabelableType
) => {
    const labelsInBothLists = intersection(
        primary.assignedLabels,
        secondary.assignedLabels,
        (l, r) => l.externalId === r.externalId
    );
    return labelsInBothLists;
};

export const getLabelsAlsoInAccount = (
    workSite: LabelableType,
    account: LabelableType
) => {
    const inAccount = getLabelIntersection(workSite, account);

    return [...inAccount].sort((a, b) => a.path.length - b.path.length);
};

/**
 * Groups service labels by their path.
 *
 * @param {LabelSummary[]} serviceLabels - The list of service labels to group.
 * @returns {GroupedServiceLabelFilter} - An object where keys are service labels and values are objects containing their child labels grouped by parent labels.
 * i.e. { 'Service:Plumbing': { 'Service:Plumbing:ServiceType': ['JobService:Plumbing:WaterHeater'] } }
 */
export const getServiceLabelsGroupedByPath = (
    serviceLabels: LabelSummary[]
): GroupedServiceLabelFilter => {
    return serviceLabels.reduce((acc, label) => {
        if (isAServiceGroup(label)) {
            acc[label.externalId] = {};
            return acc;
        }
        const serviceLabel = label.path?.[1] || undefined;
        if (serviceLabel) {
            if (!acc[serviceLabel.externalId]) {
                acc[serviceLabel.externalId] = {};
            }
            const pathLength = label.path?.length || -1;
            const labelParent =
                pathLength !== -1 ? label.path?.[pathLength - 1] : undefined;
            if (labelParent) {
                if (!acc[serviceLabel.externalId][labelParent.externalId]) {
                    acc[serviceLabel.externalId][labelParent.externalId] = [];
                }
                acc[serviceLabel.externalId][labelParent.externalId].push(
                    label.externalId
                );
            }
        }
        return acc;
    }, {});
};

/**
 * Groups non-service labels by their type.
 *
 * @param {LabelSummary[]} nonServiceLabels - The list of non-service labels to group.
 * @returns {GroupedLabelFilters} - An object where keys are label types (e.g., 'JobType', 'CustomerType') and values are arrays of label external IDs.
 * i.e. { 'JobType': ['Service:Roofing:JobType:Inspection'], 'CustomerType': ['CustomerType:Residential'] }
 */
export const getNonServiceLabelsGroupedByPath = (
    nonServiceLabels: LabelSummary[]
): GroupedLabelFilters => {
    return nonServiceLabels.reduce((acc, label) => {
        let pathStr = undefined;
        // Merge all job types into one group regardless of path
        if (isJobTypeLabel(label)) {
            pathStr = 'JobType';
        }
        if (isCustomerTypeLabel(label)) {
            pathStr = 'CustomerType';
        }
        if (!pathStr) {
            return acc;
        }
        if (!acc[pathStr]) {
            acc[pathStr] = [];
        }
        acc[pathStr].push(label.externalId);
        return acc;
    }, {});
};

/**
 * Accepts a master list of labels and a list of label ids to filter down to.
 * Returns a key/value object where:
 *      'Service' key contains all the service labels and their children (returned as GroupedServiceLabelFilter type).
 *      Other label groups (i.e. 'JobType', 'CustomerType', etc.) and their children (returned as string[] type).
 * Useful for building queries
 * @param accountList - The list of labels on the account
 * @param labels - The sub-set of labels we're interested in
 * @returns {{
 *      [x: string]: GroupedServiceLabelFilter | string[];
 * }}
 */
export const getUsedLabelsByGroup = (
    accountList: Account[],
    labels: string[]
): GroupedLabelFilters => {
    // combine the assignedLabels from all accounts and deduplicate
    const allValidLabels: Record<string, LabelSummary> = accountList.reduce(
        (acc, account) => {
            account.assignedLabels.forEach((label) => {
                acc[label.externalId] = label;
            });
            return acc;
        },
        {}
    );

    // JobType labels are technically service labels, but are treated as a separate group. Filter them out along with CustomerType, JobType, and Unknown labels
    const serviceLabels = labels
        .map((label) => allValidLabels[label])
        .filter((label) => {
            if (!label || isJobTypeLabel(label) || isCustomerTypeLabel(label)) {
                return false;
            }
            return (
                label.externalId.indexOf('JobType:') === -1 &&
                label.externalId.indexOf('Unknown:') === -1 &&
                label.externalId.indexOf('CustomerType:') === -1
            );
        })
        .sort((a, b) => a.path.length - b.path.length);

    const serviceLabelIds = serviceLabels.map((label) => label.externalId);
    const nonServiceLabelIds = labels.filter(
        (label) => serviceLabelIds.indexOf(label) === -1
    );

    const serviceLabelsGroupedByPath =
        getServiceLabelsGroupedByPath(serviceLabels);

    const nonServiceLabels = nonServiceLabelIds
        .map((label) => allValidLabels[label])
        .filter((label) => !!label)
        .sort((a, b) => a.path.length - b.path.length);

    const nonServiceLabelsGroupedByPath =
        getNonServiceLabelsGroupedByPath(nonServiceLabels);

    const hasServiceLabels = Object.keys(serviceLabelsGroupedByPath).length > 0;

    return {
        ...(hasServiceLabels && { Service: serviceLabelsGroupedByPath }),
        ...nonServiceLabelsGroupedByPath,
    };
};

export const isJobTypeLabel = (label: LabelSummary) => {
    // If the last path entry has a name of Job Type
    return (
        [...label.path].reverse()[0]?.name ===
        ServiceChildrenLabelGroupNames.jobType
    );
};

export const isCustomerTypeLabel = (label: LabelSummary) => {
    return (
        [...label.path]?.[0]?.externalId === TopLevelExternalIds.customerType
    );
};

/**
 * For checking if a label is a Service Type label
 * Examples: `JobService:Plumbing:WaterHeater` has `Service:Plumbing:ServiceType` in its path
 * @param label - The label to check
 * @returns {boolean}
 */
export const isServiceTypeLabel = (label: LabelSummary) => {
    // If the last path entry has a name of Service Type
    return (
        [...label.path].reverse()[0]?.name ===
        ServiceChildrenLabelGroupNames.serviceType
    );
};

/**
 * Regex Pattern ^[A-Za-z]+(:[A-Za-z]+)*$:
 * ^[A-Za-z]+ asserts that the externalId starts with letters.
 * (:[A-Za-z]+)*$ allows for one or more :SubCategory type additions.
 * @param label - the label to consider
 * @returns {boolean} - true if the label is a custom label
 */
export const isCustomLabel = (label: LabelSummary): boolean => {
    const standardPattern = /^[A-Za-z]+(:[A-Za-z]+)*$/;
    return !standardPattern.test(label.externalId);
};

export const isAServiceGroup = (label: LabelSummary) => {
    // If the path has only one entry and that has the externalId of Service
    return (
        label.path.length === 1 &&
        label.path[0].externalId === TopLevelExternalIds.service
    );
};

export enum ServiceChildrenLabelGroupNames {
    jobType = 'Job Type',
    serviceType = 'Service Type',
}

export enum TopLevelExternalIds {
    service = 'Service',
    customerType = 'CustomerType',
}

export const getGroupName = (label: LabelSummary): LabelSummary['name'] =>
    label.path[label.path.length - 1]?.name || '';

export const getParentLabel = (
    label: LabelSummary
): Omit<LabelSummary, 'path'> | undefined =>
    label.path ? label.path[label.path.length - 1] : undefined;

export const getServiceParent = (
    label: LabelSummary
): Omit<LabelSummary, 'path'> | undefined => {
    if (isServiceTypeLabel(label) || isJobTypeLabel(label)) {
        return label.path[1];
    }
    return undefined;
};

export const getSubLabels = (labels: LabelSummary[]) => {
    // Sort labels by path length ascending
    labels.sort((a, b) => a.path?.length - b.path?.length);
    const possibleChildren = new Set();
    const seenLabels = new Set();
    labels.forEach((label) => {
        label.path?.forEach((ancestor) => {
            if (seenLabels.has(ancestor.externalId)) {
                // We've seen this label before, so it's not a child and it's a child's ancestor
                // Delete the ancestor just in case it was added as a child
                possibleChildren.delete(ancestor.externalId);
                possibleChildren.add(label.externalId);
            }
        });
        seenLabels.add(label.externalId);
    });

    return Array.from(possibleChildren);
};

/**
 * Accepts a list of labels and returns true if any of the labels have sub-labels
 * Sub-labels are nested children
 * They are labels that have a parent label in the list
 * @param labels - The list of labels to check
 * @returns ${boolean} - True if the list contains any labels with sub-labels
 */
export const hasSubLabels = (labels: LabelSummary[]) =>
    getSubLabels(labels).length > 0;

export const labelColorByGroupName: Map<string, string> = new Map([
    ['Customer Type', 'rgba(200, 230, 201, 1)'], // green[100]
    ['Job Type', 'rgba(209, 196, 233, 1)'], // deepPurple[100]
    ['Service Type', 'rgba(187, 222, 251, 1)'], // blue[100]
    ['Custom Type', 'rgba(224, 224, 224, 1)'], // grey[300]
]);

/**
 * - Accepts a master list of labels and filters
 * down to only one of each with shared names
 * - Most reliable if the master list is within the same hierarchy
 * @param labels - The list of labels to dedupe
 * @returns {LabelSummary[]}
 */
export const getDedupedLabelsByName = (
    labels: LabelSummary[]
): LabelSummary[] => {
    const dedupedLabels = labels.reduce((acc, option) => {
        if (!acc.find((label) => label.name === option.name)) {
            acc.push(option);
        }
        return acc;
    }, [] as LabelSummary[]);

    return dedupedLabels;
};

export interface LabelHierarchyDefn {
    externalId: string;
    name: string;
    children?: LabelHierarchy;
}

export type LabelHierarchy = {
    [externalId: string]: LabelHierarchyDefn;
};

export const labelsToHierarchy = (labels: LabelSummary[]): LabelHierarchy => {
    return labels.reduce((acc, label) => {
        let iterator = acc;

        for (const ancestor of label.path || []) {
            iterator[ancestor.externalId] = {
                externalId: ancestor.externalId,
                name: ancestor.name,
                children: iterator[ancestor.externalId]?.children || {},
            };
            iterator = iterator[ancestor.externalId].children;
        }

        if (!iterator[label.externalId]) {
            iterator[label.externalId] = {
                externalId: label.externalId,
                name: label.name,
            };
        }

        return acc;
    }, {});
};

export const getLabelsByAncestor = (
    labels: LabelSummary[],
    externalId: string
): LabelSummary[] => {
    return labels.filter((label) => {
        return label.path.some(
            (ancestor) => ancestor.externalId === externalId
        );
    });
};

export const getCustomerTypeLabels = (
    labels: LabelSummary[]
): LabelSummary[] => {
    return getLabelsByAncestor(labels, 'CustomerType');
};

export const getTopLevelServiceTags = (
    labels: LabelSummary[]
): LabelSummary[] => {
    // returns the immediate children of Service
    return getLabelsByAncestor(labels, TopLevelExternalIds.service);
};

export const mapParentToChildrenLabels = (
    labels?: LabelSummary[],
    accountable?: { assignedLabels: LabelSummary[] }
): Record<LabelSummary['externalId'], LabelSummary[]> =>
    (labels || ([] as LabelSummary[]))?.reduce((acc, { externalId }) => {
        acc[externalId] =
            getLabelsByAncestor(accountable.assignedLabels ?? [], externalId) ??
            [];
        return acc;
    }, {} as Record<LabelSummary['externalId'], LabelSummary[]>);

export const isLegacyJobType = (externalId: string) => {
    return externalId?.startsWith('JobType:');
};

/**
 * For legacy job types, we are going to return all of
 * the new label jobs types. So if you pass in "JobType:NewInstallation" this function
 * will return "Service:Chimney:JobType:NewInstallation" and "Service:Fireplace:JobType:NewInstallation"
 *
 * Basically all service types.
 * @param labelSummaries
 * @param externalId
 */
export const getAllMatchingJobTypes = (
    labelSummaries: LabelSummary[] | undefined,
    externalId: string
): LabelSummary[] => {
    return (labelSummaries ?? []).filter(isJobTypeLabel).filter((label) => {
        return label.externalId.endsWith(externalId);
    });
};

export const deduplicateLabelsByName = (
    labels: LabelSummary[]
): LabelSummary[] => {
    return labels.reduce((acc, label) => {
        if (!acc.find((l) => l.name === label.name)) {
            acc.push(label);
        }
        return acc;
    }, [] as LabelSummary[]);
};
